import React, { useState } from 'react';
import { Avatar, Box, Paper, Tooltip, Typography } from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { surface } from '../../theme';

export const INK = '#202433';

/**
 * Change from the previous period. The arrow and sign carry the direction so
 * it never relies on color alone. `points` shows an absolute difference in
 * percentage points (for CTR) instead of a relative change, and
 * `formatDifference` shows an absolute difference in its own format (for
 * money that can start at zero or below). `lowerIsBetter` colors a rise as bad.
 */
export function Change({
  current,
  previous,
  points = false,
  formatDifference,
  lowerIsBetter = false,
  compact = false,
}) {
  if (current === null || current === undefined) return null;
  const absolute = points || Boolean(formatDifference);
  if (previous === null || previous === undefined || (!absolute && previous <= 0)) {
    return compact ? null : (
      <Typography variant="caption" color="text.secondary">
        No data in previous period
      </Typography>
    );
  }
  const change = absolute ? current - previous : ((current - previous) / previous) * 100;
  const rounded = Math.round(change * 10) / 10;
  if (rounded === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {compact ? 'No change' : 'No change vs previous period'}
      </Typography>
    );
  }
  const up = rounded > 0;
  const good = up !== lowerIsBetter;
  const Icon = up ? ArrowDropUpIcon : ArrowDropDownIcon;
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', color: good ? 'success.dark' : 'error.dark' }}
    >
      <Icon sx={{ fontSize: 20, ml: -0.75 }} />
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {formatDifference
          ? formatDifference(Math.abs(change))
          : `${Math.abs(rounded)}${points ? ' pts' : '%'}`}
      </Typography>
      {!compact && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 0.5, display: { xs: 'none', sm: 'inline' } }}
        >
          vs previous period
        </Typography>
      )}
    </Box>
  );
}

export function StatTile({ label, value, change, footnote }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', minWidth: 0 }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 700,
          lineHeight: 1.3,
          my: 0.5,
          color: INK,
          fontVariantNumeric: 'tabular-nums',
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </Typography>
      {change}
      {footnote && (
        <Typography variant="caption" color="text.secondary" component="div">
          {footnote}
        </Typography>
      )}
    </Paper>
  );
}

export function Section({ title, subtitle, action, children, sx }) {
  return (
    <Paper
      sx={{
        borderRadius: 2,
        background: surface.glass,
        boxShadow: 2,
        p: { xs: 2, md: 3 },
        mb: 2,
        ...sx,
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6">{title}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Box>
      )}
      {children}
    </Paper>
  );
}

export function ChannelAvatar({ channel, size = 40 }) {
  const [failed, setFailed] = useState(false);
  const initials =
    (channel.name || '?')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase() || '?';
  return (
    <Avatar
      src={!failed && channel.thumbnailUrl ? channel.thumbnailUrl : undefined}
      imgProps={{ referrerPolicy: 'no-referrer', onError: () => setFailed(true) }}
      alt=""
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        fontWeight: 700,
        bgcolor: 'primary.light',
        flexShrink: 0,
      }}
    >
      {initials}
    </Avatar>
  );
}

/**
 * Compact trend line scaled to its own minimum and maximum.
 * @param {number[]} props.values - plotted values; non-numbers are skipped
 * @param {string} props.label - accessible name for the chart
 * @param {(index: number) => React.ReactNode} [props.describePoint] - tooltip
 *   content for the hovered point, given its index in `values`; omit to
 *   disable hovering
 */
export function Sparkline({ values, label, describePoint }) {
  const theme = useTheme();
  const [hoverIndex, setHoverIndex] = useState(null);
  const width = 100;
  const height = 32;
  const padding = 4;
  const color = theme.palette.primary.main;
  const points = (values || [])
    .map((value, index) => ({ value, index }))
    .filter((point) => Number.isFinite(point.value));
  if (points.length < 2) {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" width="100%" height={height} aria-hidden>
        <line x1="0" x2={width} y1={height / 2} y2={height / 2} stroke={color} strokeOpacity="0.3" strokeWidth="1.5" />
      </svg>
    );
  }
  const max = Math.max(...points.map((point) => point.value));
  const min = Math.min(...points.map((point) => point.value));
  const x = (position) => (position / (points.length - 1)) * width;
  const y = (value) =>
    max === min
      ? height / 2
      : height - padding - ((value - min) / (max - min)) * (height - padding * 2);
  const line = points
    .map((point, position) => `${position ? 'L' : 'M'}${x(position).toFixed(2)},${y(point.value).toFixed(2)}`)
    .join(' ');
  const hovered = hoverIndex === null ? null : points[hoverIndex];

  function handlePointer(event) {
    const bounds = event.currentTarget.getBoundingClientRect();
    const position = Math.round(((event.clientX - bounds.left) / bounds.width) * (points.length - 1));
    setHoverIndex(Math.min(Math.max(position, 0), points.length - 1));
  }

  const chart = (
    <Box
      onPointerMove={describePoint ? handlePointer : undefined}
      onPointerLeave={() => setHoverIndex(null)}
      sx={{ position: 'relative', height }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
        role="img"
        aria-label={label}
        style={{ display: 'block' }}
      >
        <path d={`${line} L${width},${height} L0,${height} Z`} fill={color} fillOpacity="0.12" />
        <path
          d={line}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        {hovered && (
          <line
            x1={x(hoverIndex)}
            x2={x(hoverIndex)}
            y1="0"
            y2={height}
            stroke={theme.palette.text.secondary}
            strokeOpacity="0.5"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      {/* The SVG stretches to fit, so the marker is drawn in HTML to stay round. */}
      {hovered && (
        <Box
          sx={{
            position: 'absolute',
            left: `${(x(hoverIndex) / width) * 100}%`,
            top: y(hovered.value),
            width: 8,
            height: 8,
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            bgcolor: color,
            border: 2,
            borderColor: 'background.paper',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
  if (!describePoint) return chart;
  return (
    <Tooltip
      title={hovered ? describePoint(hovered.index) : ''}
      followCursor
      placement="top"
      enterDelay={0}
    >
      {chart}
    </Tooltip>
  );
}

// Small label + value pair used in channel rows. `note` is a line of text, or
// a list of lines; empty lines are skipped.
export function Metric({ label, value, change, note }) {
  const notes = [].concat(note).filter(Boolean);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="div" noWrap>
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{ fontWeight: 700, color: INK, fontVariantNumeric: 'tabular-nums' }}
        noWrap
      >
        {value}
      </Typography>
      {change && <Box sx={{ lineHeight: 1 }}>{change}</Box>}
      {notes.map((line) => (
        <Typography key={line} variant="caption" color="text.secondary" component="div" noWrap>
          {line}
        </Typography>
      ))}
    </Box>
  );
}
